import { DataTable, Pill } from '@bcecloud/portal-ui';
import type { PanelRep } from '@/lib/lead-panel';

export interface RepLoadTableProps {
  readonly reps: readonly PanelRep[];
  /** Stage rows, in the org's order. */
  readonly stages: readonly string[];
}

function count(value: number): string {
  return value.toLocaleString('en-US');
}

/** A measure row: its label and how to show each rep's value. */
interface MeasureRow {
  readonly label: string;
  readonly value: (rep: PanelRep) => React.ReactNode;
  readonly strong?: boolean;
}

const MEASURES: readonly MeasureRow[] = [
  { label: 'Leads today', value: (rep) => count(rep.receivedToday) },
  { label: 'Leads yesterday', value: (rep) => count(rep.receivedYesterday) },
  { label: 'Open leads', value: (rep) => count(rep.openLeads) },
  { label: 'Open opportunities', value: (rep) => count(rep.openOpportunities), strong: true },
  {
    label: 'Overdue tasks',
    value: (rep) => (
      <Pill tone={rep.overdueTasks > 0 ? 'danger' : 'neutral'}>
        {count(rep.overdueTasks)} of {count(rep.openTasks)}
      </Pill>
    ),
  },
];

/**
 * Each rep's load side by side, a column per rep: leads received today and
 * yesterday, open leads, open opportunities, overdue tasks, then open
 * opportunities in each stage. With a handful of reps this fits the page and
 * every figure lines up across them, to pick who gets the next lead.
 */
export function RepLoadTable({ reps, stages }: RepLoadTableProps): React.ReactElement {
  const repColumns = reps.map((_, index) => index + 1);
  return (
    <DataTable headers={['Measure', ...reps.map((rep) => rep.name)]} rightAlignedHeaders={repColumns} visuallyHiddenHeaders={[0]}>
      {MEASURES.map((measure) => (
        <tr key={measure.label} className="border-t border-portal-line align-middle">
          <th scope="row" className="p-3 text-left text-portal-small font-normal text-portal-ink">
            {measure.label}
          </th>
          {reps.map((rep) => (
            <td
              key={rep.id}
              className={`p-3 text-right text-portal-small tabular-nums whitespace-nowrap${measure.strong ? ' font-bold' : ''}`}
            >
              {measure.value(rep)}
            </td>
          ))}
        </tr>
      ))}
      {stages.map((stage, index) => (
        <tr key={stage} className="border-t border-portal-line align-middle">
          <th scope="row" className="p-3 text-left text-portal-small font-normal text-portal-muted">
            {index === 0 ? <span className="block text-portal-micro">Open opportunities by stage</span> : null}
            {stage}
          </th>
          {reps.map((rep) => (
            <td key={rep.id} className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
              {count(rep.stages.find((entry) => entry.stage === stage)?.count ?? 0)}
            </td>
          ))}
        </tr>
      ))}
    </DataTable>
  );
}
